import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import Toggle from '../shared/TailwindComponents/Toggle';
import {
  PiScalesLight,
  PiGavelLight,
  PiBookOpenLight,
  PiBuildingsLight,
  PiScrollLight,
} from 'react-icons/pi';
import Spinner from '../shared/TailwindComponents/Spinner';

interface SearchIndex {
  index_name: string;
  display_name: string;
  category: string;
  source_type: string;
  index_exists?: boolean;
  document_count?: number | null;
}

interface IndexSourceSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  jurisdiction?: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  legislation: { label: 'Legislation', icon: <PiScalesLight className="w-4 h-4" /> },
  case_law: { label: 'Case Law', icon: <PiGavelLight className="w-4 h-4" /> },
  preparatory_works: { label: 'Preparatory Works', icon: <PiBookOpenLight className="w-4 h-4" /> },
  regulation: { label: 'Regulations', icon: <PiScrollLight className="w-4 h-4" /> },
  agency_decisions: { label: 'Agency Decisions', icon: <PiBuildingsLight className="w-4 h-4" /> },
  gazette: { label: 'Gazette', icon: <PiBookOpenLight className="w-4 h-4" /> },
  // Bulgaria publishes parliamentary material as its own category. Without an
  // entry the raw backend string was shown, untranslated, as the heading.
  parliamentary: { label: 'Parliamentary', icon: <PiScrollLight className="w-4 h-4" /> },
  company_filings: { label: 'Company Filings', icon: <PiBuildingsLight className="w-4 h-4" /> },
};

const IndexSourceSelector: React.FC<IndexSourceSelectorProps> = ({
  value,
  onChange,
  disabled,
  jurisdiction,
}) => {
  const { t } = useTranslation();
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;

  const fetchIndexes = useCallback(async (country: string) => {
    if (!country) return;
    setLoading(true);
    try {
      const response = await apis({
        method: 'GET',
        url: '/matters/available-indexes',
        params: { country },
      });
      const indexes: SearchIndex[] = response?.data?.indexes || [];
      setAvailableIndexes(indexes);
      // Default-select the new jurisdiction's sources. Old selections are from a
      // different country and won't match. Prefer sources whose index is
      // actually deployed; if none report that flag, fall back to all.
      if (indexes.length > 0) {
        const deployed = indexes.filter((idx) => idx.index_exists !== false);
        const toSelect = (deployed.length > 0 ? deployed : indexes).map((idx) => idx.index_name);
        onChangeRef.current(toSelect);
      }
    } catch (error) {
      console.warn('[IndexSourceSelector] Failed to fetch indexes:', error);
      setAvailableIndexes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jurisdiction) {
      fetchIndexes(jurisdiction);
    } else {
      setAvailableIndexes([]);
    }
  }, [jurisdiction, fetchIndexes]);

  if (availableIndexes.length === 0 && !loading) {
    return null;
  }

  const selected = new Set(value || []);

  const toggleIndex = (indexName: string) => {
    const next = new Set(selected);
    if (next.has(indexName)) {
      next.delete(indexName);
    } else {
      next.add(indexName);
    }
    onChange(Array.from(next));
  };

  const toggleAll = () => {
    if (selected.size === availableIndexes.length) {
      onChange([]);
    } else {
      onChange(availableIndexes.map((idx) => idx.index_name));
    }
  };

  // Group by category
  const grouped = availableIndexes.reduce<Record<string, SearchIndex[]>>((acc, idx) => {
    const cat = idx.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(idx);
    return acc;
  }, {});

  const allSelected = selected.size === availableIndexes.length;

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
          <Spinner size="glyph" className="!h-4 !w-4" />
          {t('Loading available sources...')}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selected.size} / {availableIndexes.length} {t('sources selected')}
            </p>
            <button
              type="button"
              onClick={toggleAll}
              disabled={disabled}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium disabled:opacity-50"
            >
              {allSelected ? t('Deselect All') : t('Select All')}
            </button>
          </div>

          {Object.entries(grouped).map(([category, indexes]) => {
            const config = CATEGORY_CONFIG[category] || { label: category, icon: null };
            return (
              <div key={category}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  {config.icon && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {config.icon}
                    </span>
                  )}
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {t(config.label)}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {indexes.map((idx) => {
                    const isSelected = selected.has(idx.index_name);
                    return (
                      <div
                        key={idx.index_name}
                        className={`flex items-center bg-white justify-between gap-2 rounded-lg border p-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
                            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                        } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                            {idx.display_name}
                          </p>
                          {typeof idx.document_count === 'number' && (
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                              {idx.document_count.toLocaleString()} {t('docs')}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <Toggle
                            enabled={isSelected}
                            onToggle={() => toggleIndex(idx.index_name)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default IndexSourceSelector;
